



import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { signOut } from "firebase/auth";
import { collection, query, getDocs, orderBy, doc, updateDoc, where, addDoc, serverTimestamp, setDoc, getDoc, onSnapshot, increment } from "firebase/firestore";
import { auth, db } from "./firebase";
// Import options screens
import { ClientHistory, UpcomingBookings, FeedbackReviews, BookAppointment, ReportIssue, EditProfile, ViewProfile, AvailableSlots } from "./TherapistOptions";
import LiveChat from "./LiveChat";
import LiveSupportDashboard from "./LiveSupportDashboard";
import TherapistChatPopup from "./TherapistChatPopup";
import "./TherapistDashboard.css";

// --- STYLES ---
const styles = {
    // LOADING & SPINNER
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#FFF7F1'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #FFDCC9',
        borderTop: '5px solid #C57B57',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },

    // COMMON
    calendarPage: { backgroundColor: "#FFF7F1", minHeight: "100vh", position: "relative", paddingBottom: '100px', fontFamily: "'Syne', sans-serif", width: '100%', overflowX: 'hidden' },

    // DASHBOARD LAYOUT
    dashboardLayout: {
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#FFF7F1',
        fontFamily: "'Syne', sans-serif",
    },
    sidebar: {
        width: '260px',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        backgroundColor: '#FFFFFF',
        boxShadow: '4px 0 24px rgba(197, 123, 87, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
        overflowY: 'auto'
    },
    sidebarHeader: {
        padding: '30px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderBottom: '1px solid rgba(197, 123, 87, 0.1)',
        marginBottom: '20px'
    },
    sidebarName: {
        marginTop: '15px',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#003049',
        textAlign: 'center'
    },
    sidebarMenu: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0 15px',
        gap: '10px'
    },
    sidebarMenuItem: {
        padding: '12px 20px',
        borderRadius: '12px',
        border: 'none',
        background: 'transparent',
        color: '#555',
        fontSize: '15px',
        fontWeight: '600',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    sidebarMenuItemActive: {
        padding: '12px 20px',
        borderRadius: '12px',
        border: 'none',
        background: 'rgba(197, 123, 87, 0.1)',
        color: '#C57B57',
        fontSize: '15px',
        fontWeight: '700',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    sidebarLogoutBtn: {
        marginTop: 'auto',
        marginBottom: '30px',
        marginLeft: '15px',
        marginRight: '15px',
        padding: '12px 20px',
        borderRadius: '12px',
        border: '1px solid #F07167',
        background: 'transparent',
        color: '#F07167',
        fontSize: '15px',
        fontWeight: '700',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    mainContent: {
        marginLeft: '260px',
        flex: 1,
        width: 'calc(100% - 260px)',
        position: 'relative'
    },

    // HEADER
    calHeader: { padding: "20px 25px", display: "flex", justifyContent: "space-between", alignItems: "center", position: 'relative', zIndex: 10 },
    headerProfile: { position: 'absolute', top: '50px', left: '0', right: '0', display: 'flex', justifyContent: 'center', pointerEvents: 'none' },
    avatarPlaceholderLarge: { width: '90px', height: '90px', borderRadius: '50%', border: '4px solid #FFF7F1', backgroundColor: '#F4A261', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', fontWeight: 'bold', color: 'white' },
    avatarPlaceholderSmall: { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#F4A261', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', color: 'white' },
    iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer' },
    iconBtnMenu: { background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-end' },
    menuBar: { width: '28px', height: '3px', backgroundColor: '#C57B57', borderRadius: '3px' },

    // GREETING AREA
    greetingContainer: { textAlign: 'center', marginTop: '1px', marginBottom: '25px' },
    greetingTime: { fontSize: '24px', color: '#555', fontWeight: '400', margin: 0 },
    greetingName: { fontSize: '45px', color: '#C57B57', fontWeight: '700', margin: '5px 0 0', letterSpacing: '-1.5px' },

    // CALENDAR CARD
    calContent: { padding: "0 20px", textAlign: 'center' },
    instructionText: { fontSize: '14px', color: '#888', marginBottom: '15px', fontWeight: '500' },
    calendarCard: {
        backgroundColor: "rgba(255, 255, 255, 0.9)", backdropFilter: "blur(10px)", borderRadius: "24px", padding: "20px",
        boxShadow: "0 10px 40px -10px rgba(197, 123, 87, 0.3)", maxWidth: '340px', margin: '0 auto', border: '1px solid rgba(255,255,255,0.5)'
    },
    monthHeader: { marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 10px' },
    monthTitle: { fontSize: '18px', fontWeight: 'bold', color: '#333' },
    navArrow: { background: 'none', border: 'none', cursor: 'pointer', padding: '5px' },
    weekGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '15px' },
    weekDay: { fontSize: '12px', color: '#999', textAlign: 'center', fontWeight: '600' },
    daysGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' },
    dayCell: { height: '36px', width: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', cursor: 'pointer', transition: '0.2s', fontSize: '14px', color: '#444' },
    dayText: { fontSize: '14px', color: '#333' },
    emptySlot: { height: '36px' },

    // APPOINTMENTS VIEW REDESIGN
    appointmentsContainer: { padding: '30px 20px 150px', position: 'relative', zIndex: 5, marginTop: '10px' },
    apptHeaderGlass: {
        backgroundColor: "rgba(255, 255, 255, 0.8)", backdropFilter: "blur(12px)",
        borderRadius: "20px", padding: "48px 25px", marginBottom: "30px",
        boxShadow: "0 8px 32px rgba(197, 123, 87, 0.15)", border: "1px solid rgba(255, 255, 255, 0.6)",
        display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "5px"
    },
    apptTitleModern: { fontSize: '34px', fontWeight: '700', color: '#C57B57', margin: 0, letterSpacing: '-0.5px' },
    apptSubTitleModern: { fontSize: '15px', color: '#555', fontWeight: '500', margin: 0 },

    // TIMELINE LAYOUT
    timelineContainer: { position: 'relative', marginTop: '10px' },
    timelineWrapper: { position: 'relative', paddingLeft: '20px' },
    timelineLine: { position: 'absolute', top: '10px', bottom: '20px', left: '7px', width: '2px', backgroundColor: '#F0D5C3', borderRadius: '2px' },
    timelineItem: { position: 'relative', marginBottom: '15px' },
    timelineDot: { position: 'absolute', left: '-20px', top: '25px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#FFF', border: '4px solid #C57B57', zIndex: 2, boxShadow: '0 0 0 4px #FFF7F1' },

    // EMPTY STATE REDESIGN
    noDataGlassContainer: {
        backgroundColor: "rgba(255, 255, 255, 0.7)", backdropFilter: "blur(8px)",
        borderRadius: "24px", padding: "40px 20px", textAlign: "center",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)", border: "1px dashed rgba(197, 123, 87, 0.4)", marginTop: "20px"
    },
    noDataIconBox: { fontSize: "40px", marginBottom: "15px", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.1))" },
    noDataText: { fontSize: "16px", color: "#666", fontWeight: "600", marginBottom: "25px" },
    backToCalBtnModern: {
        background: "linear-gradient(135deg, #C57B57 0%, #E78B50 100%)", color: "white",
        border: "none", padding: "12px 25px", borderRadius: "25px", fontSize: "14px", fontWeight: "700",
        cursor: "pointer", boxShadow: "0 8px 20px rgba(197, 123, 87, 0.3)", transition: "all 0.3s ease"
    },

    header: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' },
    backBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '10px' },
    headerTitle: { margin: 0, fontSize: '24px', color: '#003049' },
    upcomingPage: { padding: '20px', minHeight: '100vh', backgroundColor: '#FFF7F1', paddingBottom: '100px' },
    upcomingCard: { backgroundColor: 'white', borderRadius: '20px', padding: '16px', marginBottom: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #FFDCC0' },

    // SESSION CARD REDESIGN
    sessionCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(15px)',
        borderRadius: '30px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 40px rgba(197, 123, 87, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.9)',
        marginBottom: '20px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden'
    },
    sessionCardTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        marginBottom: '20px'
    },
    sessionLeft: { display: 'flex', gap: '20px', alignItems: 'center' },
    clientAvatar: {
        width: '64px',
        height: '64px',
        borderRadius: '20px',
        objectFit: 'cover',
        boxShadow: '0 8px 16px rgba(197, 123, 87, 0.15)',
        border: '2px solid white'
    },
    clientName: { margin: '0 0 8px', fontSize: '19px', fontFamily: "'Syne', sans-serif", fontWeight: '500', color: '#003049', letterSpacing: '-0.3px' },
    clientMetaRow: { display: 'flex', gap: '8px' },
    metaTag: {
        backgroundColor: '#F3E5DC',
        color: '#C57B57',
        padding: '4px 10px',
        borderRadius: '8px',
        fontSize: '11px',
        fontWeight: '700'
    },
    notesHistoryWrapper: { textAlign: 'right' },
    historyLabel: { display: 'block', fontSize: '11px', fontWeight: '700', color: '#A0AEC0', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '0.5px' },
    bubblesContainer: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
    bubble: {
        width: '38px',
        height: '38px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
        position: 'relative'
    },
    noteDot: {
        position: 'absolute', top: '-4px', right: '-4px', width: '12px', height: '12px',
        backgroundColor: '#F07167', borderRadius: '50%', border: '2px solid white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    sessionActionRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '15px',
        borderTop: '1px solid #F1F2F6'
    },
    statusIndicator: { display: 'flex', alignItems: 'center' },
    statusCompleted: { color: '#003049', fontSize: '13px', fontWeight: '700' },
    statusExpired: { color: '#F07167', fontSize: '13px', fontWeight: '700' },
    statusReady: {
        color: '#00B894',
        fontSize: '13px',
        fontWeight: '700',
        animation: 'pulse 1.5s ease-in-out infinite',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    statusPending: { color: '#747D8C', fontSize: '13px', fontWeight: '600' },
    startSessionBtnPremium: {
        display: 'flex', alignItems: 'center', gap: '8px',
        background: 'linear-gradient(135deg, #F07167 0%, #FF8A71 100%)',
        color: 'white', border: 'none', padding: '10px 20px', borderRadius: '15px',
        fontSize: '13px', fontWeight: '800', cursor: 'pointer',
        boxShadow: '0 10px 20px rgba(240, 113, 103, 0.25)',
        transition: 'all 0.3s ease'
    },
    expandBtnCircle: {
        width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #E2E8F0',
        backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: '#718096', transition: 'all 0.2s ease'
    },

    // BACKGROUND
    waveContainer: { position: 'fixed', bottom: 0, left: 0, width: '100%', zIndex: 0, pointerEvents: 'none' },
    waveSvg: { display: 'block', width: '100%', height: 'auto' },

    // OPTIONS MODAL
    backdrop: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.3)", zIndex: 2000 },
    optionsSheet: { position: 'fixed', top: 0, left: 0, width: '100%', backgroundColor: '#FFDCC0', borderBottomLeftRadius: '30px', borderBottomRightRadius: '30px', padding: '30px', zIndex: 3001, minHeight: '60vh', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' },
    optionsHeader: { marginBottom: '10px' },
    optName: { fontSize: '16px', fontWeight: 'bold', color: '#000' },
    optTitle: { textAlign: 'center', fontSize: '32px', fontWeight: 'bold', color: '#C57B57', marginBottom: '30px' },
    optList: { display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' },
    optBtn: { width: '90%', padding: '16px', borderRadius: '30px', border: 'none', backgroundColor: 'white', fontSize: '16px', fontWeight: '600', color: '#222', cursor: 'pointer', boxShadow: '0 5px 15px rgba(0,0,0,0.05)' },
    dragHandle: { textAlign: 'center', marginTop: '30px', cursor: 'pointer', transform: 'rotate(180deg)' },

    // PROFILE SIDEBAR
    profileSidebar: { position: 'fixed', top: 0, right: 0, width: '100%', maxWidth: '350px', height: '100vh', backgroundColor: '#FFF7F1', zIndex: 3002, boxShadow: '-5px 0 30px rgba(0,0,0,0.1)', overflowY: 'auto' },

    // MODALS (Notes & Start Session)
    notesModal: { position: 'fixed', top: '20%', left: '5%', right: '5%', width: '90%', maxWidth: '400px', margin: '0 auto', backgroundColor: 'white', borderRadius: '24px', padding: '30px', zIndex: 3001, textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' },
    modalHeaderBasic: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    backBtnSimple: { background: 'none', border: 'none', cursor: 'pointer' },
    notesMainTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' },
    notesArea: { minHeight: '150px', fontSize: '14px', color: '#333' },
    notesInput: { width: '100%', height: '120px', padding: '5px', borderRadius: '12px', border: '1px solid #eee', backgroundColor: '#FAFAFA', fontSize: '14px', fontFamily: "'Syne', sans-serif", resize: 'none', outline: 'none' },
    readMoreBtn: { padding: '12px 30px', backgroundColor: '#FFDCC0', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', marginTop: '20px', color: '#555' },

    lastSavedBadge: {
        backgroundColor: '#E3F2FD',
        color: '#1976D2',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '9px',
        fontWeight: 'bold'
    },
    savingBadge: {
        backgroundColor: '#FFF3E0',
        color: '#F57C00',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '9px',
        fontWeight: 'bold',
        animation: 'pulse 1s infinite'
    },
    floatingChatBtn: {
        position: 'fixed',
        bottom: '30px',
        right: '30px',
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        backgroundColor: '#003049',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        boxShadow: '0 8px 25px rgba(0,48,73,0.3)',
        cursor: 'pointer',
        zIndex: 5000,
        border: 'none'
    },
    manualCompleteCheckbox: {
        width: '22px',
        height: '22px',
        borderRadius: '6px',
        border: '2px solid #C57B57',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: 'white',
        color: 'white',
        fontSize: '14px',
        fontWeight: 'bold'
    },
    checkboxChecked: {
        backgroundColor: '#003049',
        borderColor: '#003049',
    },
    // START SESSION SPECIFIC STYLES
    emailLabel: { display: 'block', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '5px' },
    emailInput: { width: '100%', padding: '12px 15px', borderRadius: '12px', border: '1px solid #ddd', backgroundColor: '#FAFAFA', fontSize: '14px', outline: 'none', marginBottom: '15px', boxSizing: 'border-box' },
    startSessionBtnBig: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F07167', color: 'white', border: 'none', padding: '14px', borderRadius: '15px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(240, 113, 103, 0.4)' },
    sessionInfo: {
        margin: '4px 0 2px',
        fontSize: '12px',
        color: '#C57B57',
        fontWeight: '600'
    },

    progressInfo: {
        margin: '2px 0 0',
        fontSize: '11px',
        color: '#666'
    },

    sessionRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '10px'
    },

    startSessionBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: '#F07167',
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 3px 10px rgba(240, 113, 103, 0.3)',
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap',
        ':hover': {
            backgroundColor: '#E05A50',
            transform: 'translateY(-1px)'
        }
    },

    completedBadge: {
        backgroundColor: '#E8F5E9',
        color: '#4CAF50',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap'
    },

    pendingBadge: {
        backgroundColor: '#FFF3E0',
        color: '#F57C00',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap'
    },

    missedBadge: {
        backgroundColor: '#FFEBEE',
        color: '#D32F2F',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        border: '1px solid #FFCDD2'
    },

    accordionContent: {
        marginTop: '15px',
        paddingTop: '15px',
        borderTop: '1px solid #F0E0D0',
        width: '100%',
        fontFamily: "'Syne', sans-serif"
    },
    detailsSection: {
        marginBottom: '15px',
        padding: '12px',
        backgroundColor: '#FAFAFA',
        borderRadius: '12px',
        fontFamily: "'Syne', sans-serif"
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#C57B57',
        marginBottom: '10px',
        marginTop: '0',
        fontFamily: "'Syne', sans-serif"
    },
    detailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '10px',
        fontSize: '13px',
        color: '#444',
        fontFamily: "'Syne', sans-serif"
    },
    consentWarning: {
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#FFEBEE',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#D32F2F',
        fontWeight: 'bold',
        fontFamily: "'Syne', sans-serif"
    },
    consentSuccess: {
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#E8F5E9',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#4CAF50',
        fontFamily: "'Syne', sans-serif"
    },
    notesText: {
        fontSize: '13px',
        color: '#555',
        lineHeight: '1.5',
        padding: '8px',
        backgroundColor: '#FFF7F1',
        borderRadius: '8px',
        fontFamily: "'Syne', sans-serif"
    },
    activityBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'linear-gradient(135deg, #f07167  0%, #dba555 100%)',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '15px',
        fontSize: '13px',
        fontWeight: '800',
        cursor: 'pointer',
        boxShadow: '0 10px 20px rgba(42, 157, 143, 0.25)',
        transition: 'all 0.3s ease'
    },
};




// --- ICONS (SVGs) ---

const ArrowLeftIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
    </svg>
);

const ChevronLeftSmall = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
);

const ChevronRightSmall = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
);

const VideoIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="23 7 16 12 23 17 23 7"></polygon>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
    </svg>
);

const ChevronUpIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="18 15 12 9 6 15"></polyline>
    </svg>
);
const CheckSquareIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4"></polyline>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
    </svg>
);

// --- HELPER FUNCTIONS ---
const getLocalDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();
};

const isPastDateTime = (dateStr, timeSlot) => {
    if (!dateStr || !timeSlot) return false;

    // Parse the timeSlot (expected format like "11:22 AM" or "4:00 pm - 5:00 pm")
    let sessionTime = timeSlot;
    if (timeSlot.includes(' - ')) {
        sessionTime = timeSlot.split(' - ')[0]; // Take start time
    }

    // Parse date and time
    const [time, modifier] = sessionTime.trim().split(' ');
    let [hours, minutes] = time.split(':');

    // Convert to 24-hour format
    let hour24 = parseInt(hours);
    if (modifier && modifier.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
    if (modifier && modifier.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;

    // Create Date object for the session
    const [year, month, day] = dateStr.split('-');
    const sessionDateTime = new Date(year, month - 1, day, hour24, parseInt(minutes));

    // Compare with current time
    const now = new Date();
    return sessionDateTime < now;
};
const isPastDate = (dateToCheck) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(dateToCheck);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
};

export default function TherapistDashboard() {
    const { user } = useOutletContext();
    const navigate = useNavigate();
    const [screen, setScreen] = useState("calendar");
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) setIsSidebarCollapsed(true);
            else setIsSidebarCollapsed(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogoutLocal = async () => {
        try {
            const { signOut } = await import("firebase/auth");
            const { auth } = await import("./firebase");
            await signOut(auth);
            navigate("/");
        } catch (err) {
            console.error("Logout error", err);
        }
    };

    console.log(user, "userdata")

    // Profile State
    const [therapistProfile, setTherapistProfile] = useState({
        displayName: user?.displayName || user?.name || user?.email?.split('@')[0] || "Therapist",
        email: user?.email || "",
        bio: "",
        qualification: "",
        therapistId: "" // Added to track custom ID like 'jahnavi'
    });

    const [therapistAvailability, setTherapistAvailability] = useState(null);


    // Calendar States
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [sessionActivitiesMap, setSessionActivitiesMap] = useState({}); // New state for activities

    // Modal States
    const [showOptions, setShowOptions] = useState(false);
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);
    const [showNotes, setShowNotes] = useState(null);
    const [showProfile, setShowProfile] = useState(false);
    const [sessionToStart, setSessionToStart] = useState(null); // New state for Start Session Modal
    const [sessionToComplete, setSessionToComplete] = useState(null); // New state for Manual Completion Modal
    const [sessionToChat, setSessionToChat] = useState(null); // New state for Active Live Chat
    const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);
    const [showActivity, setShowActivity] = useState(null); // New state for Activity Modal

    // posting the therapist data with uid from users to therapist_avalibity into firebase tables
    const adduidintotherapistavailbitytab = async (user) => {
        console.log("came here data in ", user)
        try {
            if (!user?.uid) {
                console.error("User UID not found",);
                return;
            }

            await setDoc(
                doc(db, "therapist_availability", user.uid),
                {
                    uid: user.uid,
                    name: user.displayName || user.name || "Therapist",
                    email: user.email || "",
                    createdAt: serverTimestamp()
                },
                { merge: true } //  key line
            );

            console.log("Therapist availability added successfully");
        } catch (error) {
            console.error("Error adding availability:", error);
        }
    }

    useEffect(() => {
        if (user) {
            setTherapistProfile(prev => ({
                ...prev,
                displayName: user?.displayName || user?.name || user?.email?.split('@')[0] || "Therapist",
                email: user?.email || "",
            }));
        }
    }, [user]);


    useEffect(() => {
        adduidintotherapistavailbitytab(user);
    }, [user]);

    // --- FETCH UNREAD CHATS (Real-time) ---
    useEffect(() => {
        if (!user?.uid) return;

        const effectiveTid = therapistProfile?.therapistId || user.uid;
        const tids = [...new Set([effectiveTid, user.uid].filter(Boolean))];

        const unreadMap = {};

        const updateCount = () => {
            let total = 0;
            Object.values(unreadMap).forEach(count => total += count);
            setTotalUnreadCount(total);
        };

        const q1 = query(collection(db, "chat_sessions"), where("therapistId", "in", tids));
        const unsub1 = onSnapshot(q1, (snapshot) => {
            snapshot.forEach(doc => {
                const data = doc.data();
                unreadMap[doc.id] = data.unreadCountByTherapist > 0 ? data.unreadCountByTherapist : (data.unreadByTherapist ? 1 : 0);
            });
            updateCount();
        });

        let unsub2 = () => { };
        if (user.email || therapistProfile?.email) {
            const emailToUse = user.email || therapistProfile.email;
            const q2 = query(collection(db, "chat_sessions"), where("therapistEmail", "==", emailToUse));
            unsub2 = onSnapshot(q2, (snapshot) => {
                snapshot.forEach(doc => {
                    const data = doc.data();
                    unreadMap[doc.id] = data.unreadCountByTherapist > 0 ? data.unreadCountByTherapist : (data.unreadByTherapist ? 1 : 0);
                });
                updateCount();
            });
        }

        return () => { unsub1(); unsub2(); };
    }, [user, therapistProfile?.therapistId, therapistProfile?.email]);

    // --- FETCH BOOKINGS (Real-time) ---
    useEffect(() => {
        if (!user?.email && !therapistProfile?.email) return;

        const bookingsRef = collection(db, "bookings");
        const q = query(
            bookingsRef,
            where("therapistEmail", "==", user?.email || therapistProfile?.email || " ")
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            let dbBookings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // OPTIMIZED: Fetch all notes for this therapist in ONE query
            try {
                const notesQ = query(
                    collection(db, "therapist_notes"),
                    where("therapistId", "==", user.uid)
                );
                const notesSnapshot = await getDocs(notesQ);
                const notesMap = {};
                notesSnapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.bookingId) notesMap[data.bookingId] = data.notes || {};
                });

                // Attach notes to bookings
                dbBookings = dbBookings.map(booking => ({
                    ...booking,
                    notes: notesMap[booking.id] || {}
                }));
            } catch (noteErr) {
                console.error("Error fetching notes batch:", noteErr);
            }

            dbBookings.sort((a, b) => new Date(a.date) - new Date(b.date));
            setBookings(dbBookings);
            setLoading(false); // STOP LOADING
        });

        return () => unsubscribe();
    }, [user?.email, therapistProfile?.email, user?.uid]);

    // --- NEW: FETCH ACTIVITIES (Real-time) ---
    useEffect(() => {
        const activeEmail = (user?.email || therapistProfile?.email || "").toLowerCase().trim();
        if (!activeEmail) return;

        const activitiesRef = collection(db, "session_activities");
        const q = query(
            activitiesRef,
            where("therapistEmail", "==", activeEmail)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const activitiesMap = {};
            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.bookingId && data.sessionNumber) {
                    activitiesMap[`${data.bookingId}_${data.sessionNumber}`] = data.activities || [];
                }
            });
            setSessionActivitiesMap(activitiesMap);
        });

        return () => unsubscribe();
    }, [user?.email, therapistProfile?.email]);

    useEffect(() => {
        const fetchTherapistData = async () => {
            if (!user?.uid) return;
            try {
                // Fetch Availability
                const docRef = doc(db, "therapist_availability", user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setTherapistAvailability(docSnap.data());
                }

                // Fetch Therapist Details from 'therapists' collection
                const therapistsRef = collection(db, "therapists");
                const qTherapist = query(therapistsRef, where("email", "==", user.email));
                const therapistSnap = await getDocs(qTherapist);

                if (!therapistSnap.empty) {
                    const tData = therapistSnap.docs[0].data();
                    setTherapistProfile(prev => ({
                        ...prev,
                        bio: tData.bio || "",
                        qualification: tData.qual || tData.qualification || "",
                        location: tData.location || "",
                        image: tData.image || "",
                        introVideo: tData.introVideo || "",
                        therapistId: tData.therapistId || therapistSnap.docs[0].id
                    }));
                }
            } catch (err) {
                console.error("Error fetching therapist data:", err);
            }
        };
        fetchTherapistData();
    }, [user, therapistProfile.displayName]);
    // NEW FUNCTION: Manually mark session as completed
    const handleMarkSessionCompleted = async (booking, sessionNumber) => {
        if (!user?.uid) {
            alert("User not authenticated");
            return false;
        }

        try {
            const bookingRef = doc(db, "bookings", booking.id);
            const sessionKey = `session_${sessionNumber}`;

            // Get current session data
            const currentSessionData = booking[sessionKey] || {};

            // Update the session as completed (manually)
            await updateDoc(bookingRef, {
                [sessionKey]: {
                    ...currentSessionData,
                    isCompleted: true,  // NEW FIELD: manual completion
                    isTaken: true,      // Also mark as taken for consistency
                    completedManually: true,  // Flag to know it was manual
                    completedAt: serverTimestamp(),
                    completedBy: user?.email
                },
                // Also update root isTaken if it's session 1
                ...(sessionNumber === 1 ? { isTaken: true } : {}),
                // Increment used sessions
                sessionsUsed: increment(1),
                bookingsUsed: increment(1),
                lastSessionDate: serverTimestamp()
            });

            // Update local state
            setBookings(prev => prev.map(b => {
                if (b.id === booking.id) {
                    const updatedBooking = { ...b };
                    if (!updatedBooking[sessionKey]) updatedBooking[sessionKey] = {};
                    updatedBooking[sessionKey].isCompleted = true;
                    updatedBooking[sessionKey].isTaken = true;
                    updatedBooking[sessionKey].completedManually = true;
                    return updatedBooking;
                }
                return b;
            }));

            alert(`Session ${sessionNumber} marked as completed!`);
            return true;
        } catch (error) {
            console.error("Error marking session completed:", error);
            alert("Failed to mark session: " + error.message);
            return false;
        }
    };


    // CHANGED: Modified to save notes per session number
    // CHANGED: Added booking parameter to access fullName
    const handleSaveNote = async (bookingId, newNote, sessionNumber, patientEmail, patientName, tags = [], tableData = {}) => {
        if (!user?.uid) {
            alert("User not authenticated");
            return false;
        }

        try {
            const noteDocId = `${bookingId}_${user.uid}`;
            const noteRef = doc(db, "therapist_notes", noteDocId);

            // Get existing notes, tags, and table object
            let existingNotes = {};
            let existingTags = {};
            let existingTableNotes = {};
            const noteDoc = await getDoc(noteRef);
            if (noteDoc.exists()) {
                existingNotes = noteDoc.data().notes || {};
                existingTags = noteDoc.data().sessionTags || {};
                existingTableNotes = noteDoc.data().tableNotes || {};
            }

            // Update specific session note, tags, and table
            existingNotes[`session_${sessionNumber}`] = newNote;
            existingTags[`session_${sessionNumber}`] = tags;
            existingTableNotes[`session_${sessionNumber}`] = tableData;

            await setDoc(noteRef, {
                bookingId: bookingId,
                therapistId: user.uid,
                therapistEmail: user?.email || therapistProfile?.email,
                patientEmail: patientEmail,
                patientName: patientName || "",
                notes: existingNotes,
                sessionTags: existingTags,
                tableNotes: existingTableNotes,
                lastUpdated: serverTimestamp(),
                updatedAt: new Date().toISOString()
            }, { merge: true });

            // Update local state
            setBookings(prev => prev.map(b =>
                b.id === bookingId ? { ...b, notes: existingNotes, sessionTags: existingTags, tableNotes: existingTableNotes } : b
            ));

            alert(`Note for session ${sessionNumber} saved successfully!`);
            return true;
        } catch (error) {
            console.error("Error saving note:", error);
            alert("Failed to save note: " + error.message);
            return false;
        }
    };

    const handlePrevMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    const handleNextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 18) return "Good afternoon";
        return "Good evening";
    };





    // Update the renderCalendarDays function to check all session dates
    const renderCalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentMonth.getMonth(), currentMonth.getFullYear());
        const firstDay = getFirstDayOfMonth(currentMonth.getMonth(), currentMonth.getFullYear());
        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} style={styles.emptySlot} className="empty-slot"></div>);
        }

        const today = new Date();

        for (let day = 1; day <= daysInMonth; day++) {
            const dateToCheck = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateString = getLocalDateStr(dateToCheck);
            const isToday = isSameDay(dateToCheck, today);

            // Check if ANY session exists on this date
            let hasSession = false;
            bookings.forEach(booking => {
                const sessionCount = parseInt(booking.sessionCount) || 1;
                for (let i = 1; i <= sessionCount; i++) {
                    const session = booking[`session_${i}`];
                    if (session && session.date === dateString) {
                        hasSession = true;
                        break;
                    }
                }
            });

            let dayStyle = styles.dayCell;
            let textStyle = styles.dayText;

            if (isToday) {
                dayStyle = { ...dayStyle, backgroundColor: '#FFD8BE', border: '1px solid #C57B57', color: '#003049' };
                textStyle = { ...textStyle, color: '#003049', fontWeight: 'bold' };
            } else if (hasSession) {
                dayStyle = { ...dayStyle, backgroundColor: '#F07167', color: 'white', boxShadow: '0 4px 10px rgba(240, 113, 103, 0.4)' };
                textStyle = { ...textStyle, color: 'white', fontWeight: 'bold' };
            }

            days.push(
                <div key={day} style={dayStyle} className="day-cell" onClick={() => { setSelectedDate(dateToCheck); setScreen("appointments"); }}>
                    <span style={textStyle} className="day-text">{day}</span>
                </div>
            );
        }
        return days;
    };

    const getDailyAppointments = () => {
        if (!selectedDate) return [];

        console.log("Selected Date:", selectedDate);
        const dateStr = getLocalDateStr(selectedDate);
        return bookings.filter(b => b.date === dateStr);
    };

    // Helper function to flatten all sessions from all bookings

    const getAllSessionsForDate = (date) => {
        if (!date) return [];
        const dateStr = getLocalDateStr(date);
        const allSessions = [];

        console.log(`[getAllSessionsForDate] Checking date: ${dateStr} across ${bookings.length} bookings`);

        bookings.forEach(booking => {
            const sessionCount = parseInt(booking.sessionCount) || 1;

            for (let i = 1; i <= sessionCount; i++) {
                const sessionKey = `session_${i}`;
                let session = booking[sessionKey];

                // Fallback for session 1 if root date matches
                if (i === 1 && (!session || !session.date || session.date === "TBD")) {
                    if (booking.date && booking.date !== "TBD") {
                        session = {
                            date: booking.date,
                            time: booking.timeSlot,
                            isTaken: booking.isTaken,
                            meetLink: booking.meetLink
                        };
                    }
                }

                if (session && session.date === dateStr) {
                    console.log(`[getAllSessionsForDate] Match found! Booking: ${booking.id} | Client: ${booking.fullName} | Session: ${i} | Time: ${session.time || session.timeSlot}`);
                    allSessions.push({
                        ...booking,
                        currentSessionNumber: i,
                        sessionTime: session.time || session.timeSlot,
                        sessionDate: session.date,
                        sessionMeetLink: session.meetLink || booking.meetLink,
                        sessionIsTaken: session.isTaken || false,
                        sessionNotes: booking.notes?.[sessionKey] || "",
                        sessionData: session,
                        meetLink: session.meetLink || booking.meetLink
                    });
                }
            }
        });

        return allSessions.sort((a, b) => {
            const timeA = a.sessionTime || "";
            const timeB = b.sessionTime || "";
            return timeA.localeCompare(timeB);
        });
    };

    // --- LOADING SCREEN ---
    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={{ marginTop: '20px', fontFamily: "'Syne', sans-serif", color: '#C57B57', fontWeight: '600' }}>Loading Dashboard...</p>
            </div>
        );
    }

    const renderContent = () => {
        if (screen === "liveSupport") {
            return (
                <LiveSupportDashboard
                    user={user}
                    onBack={() => setScreen("calendar")}
                    onAccept={(sessionId) => {
                        setSessionToChat(sessionId);
                        setScreen("chat");
                    }}
                />
            );
        }

        if (screen === "chat" && sessionToChat) {
            return (
                <div style={{ ...styles.calendarPage, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                    <LiveChat
                        sessionId={sessionToChat}
                        currentUser={{ ...user, uid: user.uid, role: 'therapist' }}
                        onEnd={() => setScreen("calendar")}
                    />
                    <button
                        onClick={() => setScreen("calendar")}
                        style={{ position: 'fixed', top: '20px', left: '20px', padding: '10px 20px', background: '#FFF', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', cursor: 'pointer', zIndex: 10000 }}
                    >
                        ← Back to Dashboard
                    </button>
                </div>
            );
        }

        if (["clientHistory", "upcomingBookings", "feedback", "bookAppointment", "reportIssue", "editProfile", "availableSlots"].includes(screen)) {
            const commonProps = { bookings: bookings, onBack: () => setScreen("calendar"), setScreen };

            if (screen === "clientHistory") return <ClientHistory {...commonProps} />;
            if (screen === "upcomingBookings") return <UpcomingBookings {...commonProps} />;
            if (screen === "feedback") return <FeedbackReviews onBack={() => setScreen("calendar")} therapistId={therapistProfile.therapistId || user.uid} therapistEmail={user.email} />;
            if (screen === "bookAppointment") return <BookAppointment onBack={() => setScreen("calendar")} />;
            if (screen === "reportIssue") return <ReportIssue onBack={() => setScreen("calendar")} />;
            if (screen === "editProfile") return <EditProfile user={{ ...user, ...therapistProfile }} onBack={() => setScreen("calendar")} onProfileUpdate={(newData) => setTherapistProfile(prev => ({ ...prev, ...newData }))} />;
            if (screen === "availableSlots") return <AvailableSlots user={{ ...user, ...therapistProfile }} onBack={() => setScreen("calendar")} />;
        }

        if (screen === "calendar") {
            return (
                <div style={{ ...styles.calendarPage, minHeight: 'auto', backgroundColor: 'transparent' }}>
                    <div style={styles.calContent} className="cal-content">
                        <div style={styles.greetingContainer} className="greeting-container">
                            <p style={styles.greetingTime} className="greeting-time">{getGreeting()},</p>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                                <h2 style={styles.greetingName} className="greeting-name">{therapistProfile.displayName}</h2>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ fontSize: '32px' }}>🔔</span>
                                    {totalUnreadCount > 0 && (
                                        <span style={{ position: 'absolute', top: '0px', right: '-5px', backgroundColor: '#F07167', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                            {totalUnreadCount}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <p style={styles.instructionText} className="instruction-text">Select a date to view assigned clients:</p>

                        <div style={styles.calendarCard} className="calendar-card">
                            <div style={styles.monthHeader} className="month-header">
                                <button onClick={handlePrevMonth} style={styles.navArrow} className="nav-arrow"><ChevronLeftSmall /></button>
                                <span style={styles.monthTitle} className="month-title">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                                <button onClick={handleNextMonth} style={styles.navArrow} className="nav-arrow"><ChevronRightSmall /></button>
                            </div>
                            <div style={styles.weekGrid} className="week-grid">
                                {['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr'].map(d => (<span key={d} style={styles.weekDay} className="week-day">{d}</span>))}
                            </div>
                            <div style={styles.daysGrid} className="days-grid">{renderCalendarDays()}</div>
                        </div>
                    </div>

                    <div style={styles.waveContainer} className="wave-container">
                        <svg viewBox="0 0 1440 320" style={styles.waveSvg} className="wave-svg">
                            <path fill="#C57B57" fillOpacity="1" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                        </svg>
                        <svg viewBox="0 0 1440 320" style={{ ...styles.waveSvg, position: 'absolute', bottom: 0, opacity: 0.6 }} className="wave-svg">
                            <path fill="#A05A3A" fillOpacity="1" d="M0,256L48,229.3C96,203,192,149,288,154.7C384,160,480,224,576,218.7C672,213,768,139,864,128C960,117,1056,171,1152,197.3C1248,224,1344,224,1392,224L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                        </svg>
                    </div>
                </div>
            );
        }

        if (screen === "appointments") {
            return (
                <div style={{ ...styles.calendarPage, minHeight: 'auto', backgroundColor: 'transparent' }}>
                    <div style={styles.calHeader}>
                        <button onClick={() => setScreen("calendar")} style={styles.iconBtn}><ArrowLeftIcon /></button>
                    </div>

                    <div style={styles.appointmentsContainer} className="appointments-container">
                        <div style={styles.apptHeaderGlass} className="appt-header-glass">
                            <h3 style={styles.apptTitleModern} className="appt-title-modern">Appointments</h3>
                            <span style={styles.apptSubTitleModern} className="appt-subtitle-modern">
                                {isSameDay(selectedDate, new Date()) ? "Today, " : ""}
                                {selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                        </div>

                        <div style={styles.timelineContainer}>
                            {getAllSessionsForDate(selectedDate).length > 0 ? (
                                <div style={styles.timelineWrapper}>
                                    <div style={styles.timelineLine}></div>
                                    {getAllSessionsForDate(selectedDate).map((sessionBooking, idx) => (
                                        <div key={`${sessionBooking.id}-session-${sessionBooking.currentSessionNumber}`} style={styles.timelineItem}>
                                            <div style={styles.timelineDot}></div>
                                            <SessionCard
                                                booking={sessionBooking}
                                                availability={therapistAvailability}
                                                sessionActivitiesMap={sessionActivitiesMap}
                                                onNotesClick={(booking, sessionNum) => {
                                                    setShowNotes({ booking, sessionNumber: sessionNum });
                                                }}
                                                onStartSessionClick={(booking, sessionNum) => {
                                                    setSessionToStart({ ...booking, sessionNumber: sessionNum });
                                                }}
                                                onMarkCompletedClick={(booking, sessionNum) => {
                                                    setSessionToComplete({ booking, sessionNumber: sessionNum });
                                                }}
                                                onActivityClick={(booking, sessionNum) => {
                                                    setShowActivity({ booking, sessionNumber: sessionNum });
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={styles.noDataGlassContainer}>
                                    <div style={styles.noDataIconBox}>☕</div>
                                    <p style={styles.noDataText}>Your schedule is wonderfully clear today.</p>
                                    <button style={styles.backToCalBtnModern} onClick={() => setScreen("calendar")}>
                                        Check Another Date
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={styles.waveContainer} className="wave-container">
                        <svg viewBox="0 0 1440 320" style={styles.waveSvg} className="wave-svg">
                            <path fill="#C57B57" fillOpacity="1" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                        </svg>
                    </div>
                </div>
            );
        }
        return null;
    };

    const getMenuItemStyle = (targetScreen) => {
        const baseStyle = screen === targetScreen ? styles.sidebarMenuItemActive : styles.sidebarMenuItem;
        return {
            ...baseStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
            gap: '10px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        };
    };

    const sidebarStyle = {
        ...styles.sidebar,
        width: isSidebarCollapsed ? '80px' : '260px',
        transition: 'width 0.3s ease'
    };

    const mainContentStyle = {
        ...styles.mainContent,
        marginLeft: isSidebarCollapsed ? '80px' : '260px',
        width: isSidebarCollapsed ? 'calc(100% - 80px)' : 'calc(100% - 260px)',
        transition: 'all 0.3s ease'
    };

    return (
        <div style={styles.dashboardLayout}>
            {/* Sidebar */}
            <div style={sidebarStyle}>
                <div
                    style={{ position: 'absolute', top: '15px', right: isSidebarCollapsed ? '30px' : '15px', cursor: 'pointer', zIndex: 101 }}
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                >
                    {isSidebarCollapsed ? <ChevronRightSmall /> : <ChevronLeftSmall />}
                </div>

                <div style={styles.sidebarHeader}>
                    <div style={{ ...styles.avatarPlaceholderLarge, width: isSidebarCollapsed ? '50px' : '90px', height: isSidebarCollapsed ? '50px' : '90px', fontSize: isSidebarCollapsed ? '20px' : '32px', transition: 'all 0.3s ease' }}>
                        {therapistProfile.image ? (
                            <img src={therapistProfile.image} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} alt="Profile" />
                        ) : (
                            therapistProfile.displayName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || "TP"
                        )}
                    </div>
                    {!isSidebarCollapsed && (
                        <div style={styles.sidebarName}>{therapistProfile.displayName}</div>
                    )}
                </div>

                <div style={{ ...styles.sidebarMenu, padding: isSidebarCollapsed ? '0 10px' : '0 15px' }}>
                    <button style={getMenuItemStyle("calendar")} onClick={() => setScreen("calendar")}>
                        <span style={{ fontSize: '20px' }}>🏠</span> {!isSidebarCollapsed && "Dashboard"}
                    </button>
                    <button style={getMenuItemStyle("editProfile")} onClick={() => setScreen("editProfile")}>
                        <span style={{ fontSize: '20px' }}>👤</span> {!isSidebarCollapsed && "Edit Profile"}
                    </button>
                    <button style={getMenuItemStyle("availableSlots")} onClick={() => setScreen("availableSlots")}>
                        <span style={{ fontSize: '20px' }}>⏰</span> {!isSidebarCollapsed && "Available slots"}
                    </button>
                    <button style={getMenuItemStyle("clientHistory")} onClick={() => setScreen("clientHistory")}>
                        <span style={{ fontSize: '20px' }}>🕒</span> {!isSidebarCollapsed && "Client History"}
                    </button>
                    <button style={getMenuItemStyle("upcomingBookings")} onClick={() => setScreen("upcomingBookings")}>
                        <span style={{ fontSize: '20px' }}>📅</span> {!isSidebarCollapsed && "Upcoming Bookings"}
                    </button>
                    <button style={getMenuItemStyle("feedback")} onClick={() => setScreen("feedback")}>
                        <span style={{ fontSize: '20px' }}>⭐</span> {!isSidebarCollapsed && "Feedback"}
                    </button>
                </div>

                <button
                    style={{ ...styles.sidebarLogoutBtn, padding: isSidebarCollapsed ? '12px 0' : '12px 20px', display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center' }}
                    onClick={handleLogoutLocal}
                >
                    <span style={{ fontSize: '20px' }}>🚪</span> {!isSidebarCollapsed && "Logout"}
                </button>
            </div>

            {/* Main Content Area */}
            <div style={mainContentStyle}>
                {renderContent()}

                {/* Floating Chat Button */}
                {screen !== "chat" && screen !== "liveSupport" && (
                    <button
                        onClick={() => setIsChatPopupOpen(true)}
                        style={styles.floatingChatBtn}
                        title="Open Messages"
                    >
                        💬
                    </button>
                )}

                <AnimatePresence>
                    {isChatPopupOpen && (
                        <TherapistChatPopup
                            user={{ ...user, therapistId: therapistProfile.therapistId }}
                            onClose={() => setIsChatPopupOpen(false)}
                        />
                    )}
                </AnimatePresence>

                {/* Modals */}
                <NotesModal
                    isOpen={!!showNotes}
                    booking={showNotes?.booking || showNotes}
                    sessionNumber={showNotes?.sessionNumber || 1}
                    onClose={() => setShowNotes(null)}
                    onSave={handleSaveNote}
                />
                <StartSessionModal
                    isOpen={!!sessionToStart}
                    booking={sessionToStart}
                    onClose={() => setSessionToStart(null)}
                    therapistEmail={user?.email}
                    availability={therapistAvailability}
                />
                <ManualCompleteModal
                    isOpen={!!sessionToComplete}
                    booking={sessionToComplete?.booking}
                    sessionNumber={sessionToComplete?.sessionNumber}
                    onClose={() => setSessionToComplete(null)}
                    onConfirm={handleMarkSessionCompleted}
                />
                <ClientActivityModal
                    isOpen={!!showActivity}
                    booking={showActivity?.booking}
                    sessionNumber={showActivity?.sessionNumber}
                    onClose={() => setShowActivity(null)}
                    therapistProfile={therapistProfile}
                    userType="therapist"
                    user={user}
                    initialActivities={sessionActivitiesMap[`${showActivity?.booking?.id}_${showActivity?.sessionNumber}`] || []}
                />
            </div>
        </div>
    );
}

const SessionCard = ({ booking, availability, onNotesClick, onStartSessionClick, onMarkCompletedClick, onActivityClick, sessionActivitiesMap = {} }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    console.log(booking, "print booking data in therapist dashboard");
    const totalSessions = parseInt(booking.sessionCount) || 1;
    const currentSessionNum = booking.currentSessionNumber || 1;

    // Update current time every second to refresh button states in real-time
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000); // 1 second for precision
        return () => clearInterval(interval);
    }, []);

    // Robust time parsing to handle varied formats like "10:30 PM", "10:30PM", "22:30"
    const parseTimeTo24Hour = (timeStr) => {
        if (!timeStr) return null;

        // If it's a range "10:30 PM - 11:30 PM", take the start time
        let timePart = timeStr;
        if (timeStr.includes(' - ')) {
            timePart = timeStr.split(' - ')[0].trim();
        }

        const cleanTime = timePart.trim().toUpperCase();

        // Handle AM/PM formats
        const ampmMatch = cleanTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/) || cleanTime.match(/(\d{1,2}):(\d{2})(AM|PM)/);
        if (ampmMatch) {
            let hour = parseInt(ampmMatch[1]);
            let min = parseInt(ampmMatch[2]);
            const mod = ampmMatch[3];
            if (mod === 'PM' && hour !== 12) hour += 12;
            if (mod === 'AM' && hour === 12) hour = 0;
            return { hour24: hour, minutes: min };
        }

        // Handle 24h format (HH:mm)
        const basicMatch = cleanTime.match(/^(\d{1,2}):(\d{2})$/);
        if (basicMatch) {
            return { hour24: parseInt(basicMatch[1]), minutes: parseInt(basicMatch[2]) };
        }

        return null;
    };

    const normalizeTimeForMatch = (t) => t ? t.replace(/\s+/g, '').toUpperCase() : "";

    // Parse end time from timeSlot or fallback to availability schedule
    const parseEndTime = (dateStr, timeSlot) => {
        if (!timeSlot) return null;

        if (timeSlot.includes(' - ')) {
            const endTimeStr = timeSlot.split(' - ')[1].trim();
            const parsed = parseTimeTo24Hour(endTimeStr);
            console.log(`[parseEndTime] Range in slot for ${booking.fullName}: ${timeSlot} -> End:`, parsed);
            return parsed;
        }

        // Fallback: look up in therapist availability from Firestore
        if (availability && availability.monthlySlots && availability.monthlySlots[dateStr]) {
            const daySlots = availability.monthlySlots[dateStr];
            const startTimeStrClean = normalizeTimeForMatch(timeSlot.trim());

            const matchedSlot = daySlots.find(s => normalizeTimeForMatch(s.from) === startTimeStrClean);
            if (matchedSlot && matchedSlot.to) {
                const parsed = parseTimeTo24Hour(matchedSlot.to);
                console.log(`[parseEndTime] Firestore Match! ${booking.fullName}: ${timeSlot} matched ${matchedSlot.from}->${matchedSlot.to}`);
                return parsed;
            } else {
                console.log(`[parseEndTime] No match in Firestore for ${startTimeStrClean}. Available:`, daySlots.map(s => s.from));
            }
        } else {
            console.log(`[parseEndTime] No availability data for ${dateStr}. Availability object exists: ${!!availability}`);
        }

        return null;
    };

    // STRICT session active check - No buffers, uses currentTime state
    const isSessionActive = (dateStr, timeSlot) => {
        if (!dateStr || !timeSlot) return false;

        const startTime = parseTimeTo24Hour(timeSlot);
        if (!startTime) return false;

        const [year, month, day] = dateStr.split('-');
        const startDateTime = new Date(year, month - 1, day, startTime.hour24, startTime.minutes);

        // Handle end time
        const endTimeParsed = parseEndTime(dateStr, timeSlot);
        const endDateTime = endTimeParsed
            ? new Date(year, month - 1, day, endTimeParsed.hour24, endTimeParsed.minutes)
            : new Date(startDateTime.getTime() + 10 * 60000); // Default to 10 mins (reduced from 30)

        const now = currentTime;

        // STRICT: only allow between start and end time
        const isActive = now >= startDateTime && now <= endDateTime;

        // Debug logging for precision verification
        if (isActive || (now.getTime() > startDateTime.getTime() - 60000 && now.getTime() < endDateTime.getTime() + 60000)) {
            console.log(`[isSessionActive] ${booking.fullName} (ID: ${booking.id}) | Session ${currentSessionNum} | Time: ${timeSlot} | Now: ${now.toLocaleTimeString()} | Start: ${startDateTime.toLocaleTimeString()} | End: ${endDateTime.toLocaleTimeString()} | Active: ${isActive} | Fallback used: ${!endTimeParsed} | Availability provided: ${!!availability}`);
        }

        return isActive;
    };

    // Check if session is expired
    const isSessionExpired = (dateStr, timeSlot, isTaken) => {
        if (isTaken) return false;
        if (!dateStr || !timeSlot) return false;

        const endTime = parseEndTime(timeSlot);
        if (!endTime) return false;

        const [year, month, day] = dateStr.split('-');
        const endDateTime = new Date(year, month - 1, day, endTime.hour24, endTime.minutes);
        const now = currentTime;

        return now > endDateTime;
    };

    // UPDATED: Check if session is completed (either auto or manual)
    const isSessionCompleted = (sessionNum) => {
        const sessionKey = `session_${sessionNum}`;
        let session = booking[sessionKey];
        if (sessionNum === 1 && (!session || !session.date || session.date === "TBD")) {
            if (booking.date && booking.date !== "TBD") {
                session = { ...booking, isTaken: booking.isTaken };
            }
        }
        // Check both isTaken (auto) and isCompleted (manual)
        return session?.isTaken === true || session?.isCompleted === true;
    };

    // Get session status for display
    const getSessionStatus = (sessionNum) => {
        const sessionKey = `session_${sessionNum}`;
        let session = booking[sessionKey];

        // Fallback for session 1 if it's stored at root level
        if (sessionNum === 1 && (!session || !session.date || session.date === "TBD")) {
            if (booking.date && booking.date !== "TBD") {
                session = {
                    date: booking.date,
                    time: booking.timeSlot,
                    isTaken: booking.isTaken,
                    isCompleted: booking.isCompleted
                };
            }
        }

        if (!session || !session.date) {
            return { text: "Not Scheduled", type: "unscheduled" };
        }

        if (session.isTaken || session.isCompleted) {
            return { text: "Completed", type: "completed" };
        }

        if (isSessionExpired(session.date, session.time, false)) {
            return { text: "Expired", type: "expired" };
        }

        if (isSessionActive(session.date, session.time)) {
            const [startTime, endTime] = session.time.split(' - ');
            return { text: `Live Now (${startTime} - ${endTime})`, type: "ready" };
        }

        // Check if session is in the future
        let startTimeStr = session.time;
        if (startTimeStr.includes(' - ')) {
            startTimeStr = startTimeStr.split(' - ')[0];
        }

        const startTime = parseTimeTo24Hour(startTimeStr);
        if (startTime) {
            const [year, month, day] = session.date.split('-');
            const sessionDateTime = new Date(year, month - 1, day, startTime.hour24, startTime.minutes);
            const now = currentTime;

            if (sessionDateTime > now) {
                const minutesUntil = Math.round((sessionDateTime - now) / 60000);
                if (minutesUntil < 60) {
                    return { text: `Starts in ${minutesUntil} min`, type: "upcoming" };
                } else if (minutesUntil < 120) {
                    return { text: `Starts in ${Math.floor(minutesUntil / 60)} hour`, type: "upcoming" };
                } else {
                    const daysUntil = Math.floor(minutesUntil / (60 * 24));
                    if (daysUntil < 7) {
                        return { text: `Starts in ${daysUntil} days`, type: "upcoming" };
                    }
                }
            }
        }

        return { text: `Scheduled: ${session.time}`, type: "pending" };
    };

    // Should show start button - ONLY during exact session time
    const shouldShowStartButton = (sessionNum) => {
        const sessionKey = `session_${sessionNum}`;
        let session = booking[sessionKey];

        // Fallback for session 1
        if (sessionNum === 1 && (!session || !session.date || session.date === "TBD")) {
            if (booking.date && booking.date !== "TBD") {
                session = {
                    date: booking.date,
                    time: booking.timeSlot,
                    isTaken: booking.isTaken
                };
            }
        }

        if (!session || !session.date) return false;
        if (session.isTaken) return false;
        if (isSessionExpired(session.date, session.time, false)) return false;

        return isSessionActive(session.date, session.time);
    };

    // Get bubble style
    const getBubbleStyle = (sessionNum) => {
        const sessionKey = `session_${sessionNum}`;
        const session = booking[sessionKey];
        const status = getSessionStatus(sessionNum);

        let bubbleStyle = {
            ...styles.bubble,
            position: 'relative',
            cursor: 'pointer',
            border: 'none',
            transition: 'all 0.2s ease'
        };

        if (status.type === 'completed') {
            bubbleStyle.backgroundColor = '#003049'; // Navy Branding Color
            bubbleStyle.color = 'white';
        } else if (status.type === 'expired') {
            bubbleStyle.backgroundColor = '#F07167'; // Branding Accent (Coral)
            bubbleStyle.color = 'white';
        } else if (status.type === 'ready') {
            bubbleStyle.backgroundColor = '#F07167'; // Branding Accent (Coral)
            bubbleStyle.color = 'white';
            bubbleStyle.boxShadow = '0 0 0 2px #FFDCC0';
        } else if (status.type === 'unscheduled') {
            bubbleStyle.backgroundColor = '#F7FAFC'; // Neutral Very Light Grey
            bubbleStyle.color = '#A0AEC0';
            bubbleStyle.border = '1px dashed #CBD5E0';
            bubbleStyle.cursor = 'default';
        } else {
            // Scheduled but in future
            bubbleStyle.backgroundColor = '#FFDCC0'; // Branding Light Surface
            bubbleStyle.color = '#C57B57';
            bubbleStyle.border = '1px solid #FFDCC0';
        }

        return bubbleStyle;
    };

    return (
        <div style={{ ...styles.sessionCard, ...(isExpanded ? { boxShadow: '0 12px 40px rgba(197, 123, 87, 0.15)', border: '1px solid #C57B57' } : {}) }} className="session-card">
            {/* MAIN CONTENT AREA */}
            <div style={styles.sessionCardTop} className="session-card-top">
                {/* PATIENT INFO */}
                <div
                    style={{ ...styles.sessionLeft, cursor: 'pointer' }}
                    className="session-left"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <img
                        src={`https://ui-avatars.com/api/?name=${booking.fullName || "User"}&background=E78B50&color=white&length=1&fontsize=0.45&rounded=true`}
                        style={styles.clientAvatar}
                        alt=""
                    />
                    <div>
                        <h4 style={styles.clientName}>{booking.fullName || "Client Name"}</h4>
                        <div style={styles.clientMetaRow}>
                            <span style={styles.metaTag}>{booking.age || "?"} yrs</span>
                            <span style={styles.metaTag}>{booking.gender || "?"}</span>
                        </div>
                        <div style={{ ...styles.metaTag, width: 'fit-content', marginTop: '6px', backgroundColor: '#FFF7F1', color: '#C57B57', border: '1px solid #C57B57' }}>
                            Session {currentSessionNum}
                        </div>
                    </div>
                </div>

                {/* NOTES BUBBLES - HISTORY */}
                <div style={styles.notesHistoryWrapper} className="notes-history-wrapper">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        {(booking.appliedPromo === 'TRUSTFIRST' || Number(booking.price) === 0) && (
                            <span style={{ backgroundColor: '#F07167', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                                TRUSTFIRST
                            </span>
                        )}
                        <span style={styles.historyLabel} className="history-label">Session History (Notes)</span>
                    </div>
                    <div style={styles.bubblesContainer} className="bubbles-container">
                        {[...Array(totalSessions)].map((_, idx) => {
                            const sessionNum = idx + 1;
                            const sessionKey = `session_${sessionNum}`;
                            const session = booking[sessionKey];
                            const hasNotes = booking.notes?.[sessionKey]?.trim().length > 0;

                            return (
                                <div
                                    key={sessionNum}
                                    style={getBubbleStyle(sessionNum)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (session && session.date) {
                                            onNotesClick?.(booking, sessionNum);
                                        }
                                    }}
                                    title={hasNotes ? "Click to view notes" : "Click to add notes"}
                                >
                                    {sessionNum}
                                    {hasNotes && <span style={styles.noteDot} />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ACTION / STATUS BAR */}
            <div className="session-action-row">
                <div style={styles.statusIndicator}>
                    {(() => {
                        const currentStatus = getSessionStatus(currentSessionNum);
                        const isCompleted = currentStatus.type === 'completed';

                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div
                                    style={{
                                        ...styles.manualCompleteCheckbox,
                                        ...(isCompleted ? styles.checkboxChecked : {})
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isCompleted) {
                                            onMarkCompletedClick?.(booking, currentSessionNum);
                                        }
                                    }}
                                    title={isCompleted ? "Session completed" : "Mark as completed"}
                                >
                                    {isCompleted && "✓"}
                                </div>

                                {isCompleted ? (
                                    <span style={styles.statusCompleted}>✓ Session {currentSessionNum} Done</span>
                                ) : currentStatus.type === 'expired' ? (
                                    <span style={styles.statusExpired}>⚠️ Session {currentSessionNum} Expired</span>
                                ) : currentStatus.type === 'ready' ? (
                                    <span style={styles.statusReady}>🔴 LIVE NOW - Session {currentSessionNum} ({booking.sessionTime || booking.timeSlot})</span>
                                ) : currentStatus.type === 'upcoming' ? (
                                    <span style={styles.statusPending}>⏰ {currentStatus.text}</span>
                                ) : (
                                    <span style={styles.statusPending}>📅 {currentStatus.text}</span>
                                )}
                            </div>
                        );
                    })()}
                </div>


                <div className="session-btns-group">
                    <button
                        className="activity-btn"
                        onClick={(e) => { e.stopPropagation(); onActivityClick?.(booking, currentSessionNum); }}
                    >
                        <CheckSquareIcon />
                        <span>Activity</span>
                    </button>
                    <button
                        style={{
                            ...styles.startSessionBtnPremium,
                            opacity: shouldShowStartButton(currentSessionNum) ? 1 : 0.4,
                            cursor: shouldShowStartButton(currentSessionNum) ? 'pointer' : 'not-allowed',
                            pointerEvents: shouldShowStartButton(currentSessionNum) ? 'auto' : 'none'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (shouldShowStartButton(currentSessionNum)) {
                                onStartSessionClick?.(booking, currentSessionNum);
                            }
                        }}
                        title={shouldShowStartButton(currentSessionNum) ? "Start Session" : "Session not available yet"}
                    >
                        <VideoIcon />
                        <span>{shouldShowStartButton(currentSessionNum) ? "Start Now" : "Not Available"}</span>
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                        style={styles.expandBtnCircle}
                    >
                        <span style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', display: 'flex' }}>
                            <ChevronUpIcon />
                        </span>
                    </button>
                </div>
            </div>

            {/* Accordion Content - Expanded Details */}
            {isExpanded && (
                <div style={styles.accordionContent}>
                    {/* Personal Information Section */}
                    <div style={styles.detailsSection}>
                        <h4 style={styles.sectionTitle}>👤 Personal Information</h4>
                        <div style={styles.detailsGrid}>
                            <div><strong>Patient ID:</strong> {booking.userId || 'N/A'}</div>
                            <div><strong>Full Name:</strong> {booking.fullName || 'N/A'}</div>
                            <div><strong>Age:</strong> {booking.age || 'N/A'} years</div>
                            <div><strong>Gender:</strong> {booking.gender || 'N/A'}</div>
                            <div><strong>Email:</strong> {booking.email || 'N/A'}</div>
                            <div><strong>Phone Number:</strong> {booking.contactNumber || booking.phone || 'N/A'}</div>
                            <div><strong>Student Status:</strong> {booking.isStudent ? '✓ Yes' : '✗ No'}</div>
                            <div><strong>Message:</strong> {booking.message}</div>
                        </div>
                    </div>

                    {/* Emergency Contact Section */}
                    {(booking.emergencyContactName || booking.emergencyContactPhone) && (
                        <div style={styles.detailsSection}>
                            <h4 style={styles.sectionTitle}>🆘 Emergency Contact</h4>
                            <div style={styles.detailsGrid}>
                                <div><strong>Name:</strong> {booking.emergencyContactName || 'N/A'}</div>
                                <div><strong>Relationship:</strong> {booking.emergencyContactRelationship || 'N/A'}</div>
                                <div><strong>Phone:</strong> {booking.emergencyContactPhone || 'N/A'}</div>
                            </div>
                        </div>
                    )}

                    {/* Trust Person/Reference Section */}
                    {(booking.trustName) && (
                        <div style={styles.detailsSection}>
                            <h4 style={styles.sectionTitle}>🤝 Trust Person / Reference</h4>
                            <div style={styles.detailsGrid}>
                                <div><strong>Name:</strong> {booking.trustName || 'N/A'}</div>
                                <div><strong>Relationship:</strong> {booking.trustRelationship || 'N/A'}</div>
                                <div><strong>Phone:</strong> {booking.trustContact || 'N/A'}</div>
                            </div>
                        </div>
                    )}

                    {/* Consent Section - IMPORTANT for therapy */}
                    <div style={{ ...styles.detailsSection, backgroundColor: booking.termsAccepted ? '#E8F5E9' : '#FFEBEE' }}>
                        <h4 style={styles.sectionTitle}>📋 Consent & Agreement</h4>
                        <div style={styles.detailsGrid}>
                            <div>
                                <strong>Terms & Conditions Accepted:</strong>{' '}
                                {booking.termsAccepted ? (
                                    <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>✓ Yes</span>
                                ) : (
                                    <span style={{ color: '#F44336', fontWeight: 'bold' }}>✗ No - Consent Required!</span>
                                )}
                            </div>
                            {booking.consentDate && (
                                <div><strong>Consent Given On:</strong> {new Date(booking.consentDate).toLocaleDateString()}</div>
                            )}
                            {booking.consentSignature && (
                                <div><strong>Digital Signature:</strong> ✓ Recorded</div>
                            )}
                        </div>
                        {!booking.termsAccepted && (
                            <div style={styles.consentWarning}>
                                ⚠️ IMPORTANT: Patient has not accepted terms and conditions. Please ensure consent is obtained before proceeding with therapy.
                            </div>
                        )}
                        {booking.termsAccepted && (
                            <div style={styles.consentSuccess}>
                                ✓ Patient has provided informed consent for therapy sessions.
                            </div>
                        )}
                    </div>

                    {/* Additional Notes from Booking Form */}
                    {booking.additionalNotes && (
                        <div style={styles.detailsSection}>
                            <h4 style={styles.sectionTitle}>📝 Additional Notes</h4>
                            <div style={styles.notesText}>{booking.additionalNotes}</div>
                        </div>
                    )}

                    {/* Session Package Details */}
                    <div style={styles.detailsSection}>
                        <h4 style={styles.sectionTitle}>📦 Session Package</h4>
                        <div style={styles.detailsGrid}>
                            {(() => {
                                let completed = 0;
                                let expired = 0;
                                for (let i = 1; i <= totalSessions; i++) {
                                    const session = booking[`session_${i}`];
                                    if (session) {
                                        if (session.isTaken) completed++;
                                        else if (isSessionExpired(session.date, session.time, false)) expired++;
                                    }
                                }
                                const remaining = totalSessions - completed - expired;

                                return (
                                    <>
                                        <div><strong>Package Type:</strong> {booking.packageType || 'Standard'}</div>
                                        <div><strong>Total Sessions:</strong> {totalSessions}</div>
                                        <div><strong>Completed:</strong> <span style={{ color: '#4CAF50' }}>{completed}</span></div>
                                        <div><strong>Expired:</strong> <span style={{ color: '#F44336' }}>{expired}</span></div>
                                        <div><strong>Remaining:</strong> <span>{remaining > 0 ? remaining : 0}</span></div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const StartSessionModal = ({ isOpen, booking, onClose, therapistEmail }) => {
    console.log(booking, "booking  data her in therapist dasbh")
    const [loading, setLoading] = useState(false);

    const handleStart = async () => {
        // Check if meetLink exists in the booking
        if (!booking?.meetLink) {
            alert("Meeting link not found. Please contact support.");
            return;
        }

        // Open the meeting link immediately to prevent popup blockers
        window.open(booking.meetLink, "_blank");

        setLoading(true);

        try {
            // Update the specific session as taken
            const bookingRef = doc(db, "bookings", booking.id);

            // Determine which session to mark as taken - prioritize the passed sessionNumber
            const sessionNum = booking.sessionNumber || (booking.sessionsUsed || booking.bookingsUsed || 0) + 1;
            const sessionKey = `session_${sessionNum}`;

            await updateDoc(bookingRef, {
                [sessionKey]: {
                    ...(booking.sessionData || booking[sessionKey] || {}),
                    isStarted: true,
                    lastStartedAt: serverTimestamp(),
                    lastStartedBy: therapistEmail
                },
                lastSessionDate: serverTimestamp()
            });

            onClose();

        } catch (error) {
            console.error("Session Error:", error);
            alert("Failed to start session: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !booking) return null;

    const currentSessionNum = booking.sessionNumber || (booking.bookingsUsed || booking.sessionsUsed || 0) + 1;

    return (
        <AnimatePresence>
            {isOpen && booking && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={styles.backdrop}
                        onClick={!loading ? onClose : null}
                    />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        style={styles.notesModal}
                        className="notes-modal"
                    >
                        <div style={styles.modalHeaderBasic}>
                            <button onClick={onClose} style={styles.backBtnSimple} disabled={loading}>
                                <ArrowLeftIcon />
                            </button>
                            <div style={{ width: '28px' }}></div>
                        </div>

                        <h2 style={styles.notesMainTitle}>
                            Start Session with {booking.fullName}
                        </h2>

                        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                            <p><strong>Session:</strong> Session {currentSessionNum} of {booking.sessionCount}</p>
                            <p><strong>Date:</strong> {booking.sessionDate || booking.date}</p>
                            <p><strong>Time:</strong> {booking.sessionTime || booking.timeSlot}</p>
                            <p><strong>Meeting Link:</strong> </p>
                            <a
                                href={booking.meetLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ wordBreak: 'break-all', color: '#F07167' }}
                            >
                                {booking.meetLink}
                            </a>
                        </div>

                        <button
                            style={{
                                ...styles.startSessionBtnBig,
                                opacity: loading ? 0.7 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                            onClick={handleStart}
                            disabled={loading}
                        >
                            <VideoIcon />
                            <span style={{ marginLeft: '8px' }}>
                                {loading ? "Starting Session..." : "Send Invites & Start"}
                            </span>
                        </button>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

// NEW MODAL: Manual Completion Confirmation
const ManualCompleteModal = ({ isOpen, booking, sessionNumber, onClose, onConfirm }) => {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        await onConfirm(booking, sessionNumber);
        setLoading(false);
        onClose();
    };

    if (!isOpen || !booking) return null;

    return (
        <AnimatePresence>
            {isOpen && booking && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={styles.backdrop}
                        onClick={!loading ? onClose : null}
                    />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        style={styles.notesModal}
                        className="notes-modal"
                    >
                        <div style={styles.modalHeaderBasic}>
                            <button onClick={onClose} style={styles.backBtnSimple} disabled={loading}>
                                <ArrowLeftIcon />
                            </button>
                            <div style={{ width: '28px' }}></div>
                        </div>

                        <h2 style={styles.notesMainTitle}>
                            Mark Session as Completed?
                        </h2>

                        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                            <p><strong>Client:</strong> {booking.fullName}</p>
                            <p><strong>Session:</strong> Session {sessionNumber} of {booking.sessionCount}</p>
                            <p><strong>Date:</strong> {booking.sessionDate || booking.date}</p>
                            <p><strong>Time:</strong> {booking.sessionTime || booking.timeSlot}</p>
                        </div>

                        <div style={{
                            backgroundColor: '#FFF3E0',
                            padding: '12px',
                            borderRadius: '12px',
                            marginBottom: '20px',
                            fontSize: '13px',
                            color: '#E65100'
                        }}>
                            ⚠️ Note: This will mark the session as completed.

                        </div>

                        <button
                            style={{
                                ...styles.startSessionBtnBig,
                                opacity: loading ? 0.7 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                backgroundColor: '#003049'
                            }}
                            onClick={handleConfirm}
                            disabled={loading}
                        >
                            {loading ? "Processing..." : "✓ Yes, Mark as Completed"}
                        </button>

                        <button
                            style={{
                                ...styles.startSessionBtnBig,
                                marginTop: '10px',
                                backgroundColor: '#ccc',
                                boxShadow: 'none'
                            }}
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};



const OptionsModal = ({ isOpen, onClose, user, onNavigate }) => (
    <AnimatePresence>
        {isOpen && (
            <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={styles.backdrop} onClick={onClose} />
                <motion.div
                    initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                    transition={{ type: "spring", damping: 25, stiffness: 120 }}
                    style={styles.optionsSheet}
                    className="options-sheet"
                >
                    <div style={styles.optionsHeader}>
                        <div style={{ width: '100%', textAlign: 'left' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '10px' }}>
                                <div style={{ ...styles.avatarPlaceholderSmall, overflow: 'hidden' }}>
                                    {user.image ? (
                                        <img src={user.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Profile" />
                                    ) : (
                                        user.displayName.split(' ').map(n => n[0]).join('')
                                    )}
                                </div>
                                <h3 style={styles.optName}>{user.displayName}</h3>
                            </div>
                        </div>
                    </div>
                    <h2 style={styles.optTitle}>Options</h2>
                    <div style={styles.optList}>

                        <button style={styles.optBtn} onClick={() => onNavigate("editProfile")}>Edit Profile & Availability</button>
                        <button style={styles.optBtn} onClick={() => onNavigate("clientHistory")}>Client History</button>
                        <button style={styles.optBtn} onClick={() => onNavigate("upcomingBookings")}>Upcoming client bookings</button>
                        <button style={styles.optBtn} onClick={() => onNavigate("feedback")}>Feedback/Reviews</button>
                        {/* <button style={styles.optBtn} onClick={() => onNavigate("bookAppointment")}>Book appointment for someone</button> */}
                        {/* <button style={styles.optBtn} onClick={() => onNavigate("reportIssue")}>Report an issue</button> */}
                    </div>
                    <div style={styles.dragHandle} onClick={onClose}><ChevronUpIcon /></div>
                </motion.div>
            </>
        )}
    </AnimatePresence>
);

const ProfileSidebar = ({ isOpen, onClose, user, onEdit, onLogout }) => (
    <AnimatePresence>
        {isOpen && (
            <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={styles.backdrop} onClick={onClose} />
                <motion.div
                    initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                    transition={{ type: "spring", damping: 25, stiffness: 120 }}
                    style={styles.profileSidebar}
                    className="profile-sidebar"
                >
                    <ViewProfile user={user} onBack={onClose} onEdit={onEdit} onLogout={onLogout} isDrawer={true} />
                </motion.div>
            </>
        )}
    </AnimatePresence>
);



const NotesModal = ({ isOpen, booking, sessionNumber, onClose, onSave }) => {
    const [noteText, setNoteText] = useState("");
    const [selectedTags, setSelectedTags] = useState([]);

    const initialTableData = {
        thoughts: "",
        feelings: "",
        behavior: "",
        needs: "",
        family: "",
        friends: "",
        work: "",
        spiritual: "",
        physical: ""
    };
    const [tableData, setTableData] = useState(initialTableData);

    const [isSaving, setIsSaving] = useState(false);

    const availableTags = [
        "Scared", "Angry", "Guilt", "Lonely", "Panicked", "Anxious",
        "Sad", "Jealous", "Disappointed", "Shame", "Relationship",
        "Confused", "Childhood Trauma", "Abuse", "Psychological Abuse",
        "Emotional Abuse", "Physical Abuse", "Financial Stress",
        "Stress", "Fear", "Addiction", "Others"
    ];

    useEffect(() => {
        if (booking && sessionNumber) {
            // Load existing notes for this session
            const existingNote = booking.notes?.[`session_${sessionNumber}`] || "";
            setNoteText(existingNote);

            const existingTags = booking.sessionTags?.[`session_${sessionNumber}`] || [];
            setSelectedTags(existingTags);

            const existingTable = booking.tableNotes?.[`session_${sessionNumber}`] || initialTableData;
            setTableData(existingTable);
        }
    }, [booking, sessionNumber]);

    const handleSave = async () => {
        setIsSaving(true);
        const success = await onSave(
            booking.id,
            noteText,
            sessionNumber,
            booking.email,
            booking.fullName,
            selectedTags,
            tableData
        );
        setIsSaving(false);
        if (success) {
            onClose();
        }
    };

    const toggleTag = (tag) => {
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    const sessionExists = booking && booking[`session_${sessionNumber}`];

    if (!sessionExists) {
        return null;
    }

    return (
        <AnimatePresence>
            {isOpen && booking && (
                <>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ ...styles.backdrop, zIndex: 3000 }} onClick={onClose} />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="no-scrollbar"
                        style={{
                            position: 'fixed', top: '5%', left: '0', right: '0',
                            width: '95%', maxWidth: '650px', margin: '0 auto',
                            backgroundColor: '#FFFFFF', borderRadius: '30px',
                            padding: '25px 25px', zIndex: 3001,
                            boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
                            display: 'flex', flexDirection: 'column', gap: '15px',
                            maxHeight: '90vh', overflowY: 'auto'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', flexShrink: 0 }}>
                            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#111' }}>
                                Review the session
                            </h2>
                            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', flexShrink: 0 }}>
                            {availableTags.map(tag => {
                                const isSelected = selectedTags.includes(tag);
                                return (
                                    <button
                                        key={tag}
                                        onClick={() => toggleTag(tag)}
                                        style={{
                                            backgroundColor: isSelected ? '#D18768' : '#FDF0E6',
                                            color: isSelected ? '#FFF' : '#333',
                                            border: 'none',
                                            borderRadius: '20px',
                                            padding: '10px 22px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: isSelected ? '0 2px 8px rgba(209, 135, 104, 0.3)' : 'none'
                                        }}
                                    >
                                        {tag}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ marginTop: '5px', position: 'relative', flexShrink: 0 }}>
                            <textarea
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    border: '2px solid #F3AD87',
                                    borderRadius: '25px',
                                    padding: '20px',
                                    fontSize: '15px',
                                    color: '#333',
                                    minHeight: '100px',
                                    resize: 'vertical',
                                    outline: 'none',
                                    backgroundColor: '#FFF'
                                }}
                                placeholder={`${booking.fullName}'s Therapy notes summary...`}
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                            />
                            {/* Decorative edit icon like the design */}
                            <div style={{ position: 'absolute', bottom: '25px', right: '25px', pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: noteText ? 0.3 : 1 }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                <span style={{ fontSize: '10px', marginTop: '2px', fontWeight: '500' }}>Edit</span>
                            </div>
                        </div>

                        {/* Detailed Table section */}
                        <div style={{ overflow: 'hidden', borderRadius: '15px', border: '1px solid #F3AD87', flexShrink: 0 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    {[
                                        { key: 'thoughts', label: 'Thoughts' },
                                        { key: 'feelings', label: 'Feelings/Emotions' },
                                        { key: 'behavior', label: 'Behavior/Actions/Habits' },
                                        { key: 'needs', label: 'Needs and Wants' },
                                        { key: 'family', label: 'Family' },
                                        { key: 'friends', label: 'Friends/Relationships' },
                                        { key: 'work', label: 'Work/Finances' },
                                        { key: 'spiritual', label: 'Spiritual' },
                                        { key: 'physical', label: 'Physical Health' }
                                    ].map((row, index) => (
                                        <tr key={row.key} style={{ borderBottom: index < 8 ? '1px solid #FDECE4' : 'none' }}>
                                            <td style={{ width: '40%', padding: '16px 15px', backgroundColor: '#FDF0E6', color: '#6F4F3D', fontSize: '14px', fontWeight: 'bold', borderRight: '1px solid #FDECE4' }}>
                                                {row.label}
                                            </td>
                                            <td style={{ width: '60%', padding: '0', backgroundColor: '#FFF' }}>
                                                <input
                                                    type="text"
                                                    style={{ width: '100%', padding: '16px 15px', border: 'none', outline: 'none', boxSizing: 'border-box', fontSize: '15px', color: '#333' }}
                                                    value={tableData[row.key]}
                                                    onChange={(e) => setTableData({ ...tableData, [row.key]: e.target.value })}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px', flexShrink: 0 }}>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                style={{
                                    backgroundColor: '#D18768',
                                    color: '#FFF',
                                    border: 'none',
                                    borderRadius: '25px',
                                    padding: '15px 60px',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    cursor: isSaving ? 'not-allowed' : 'pointer',
                                    opacity: isSaving ? 0.7 : 1,
                                    boxShadow: '0 4px 12px rgba(209, 135, 104, 0.3)'
                                }}
                            >
                                {isSaving ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

// NEW MODAL: Client Activity Modal
const ClientActivityModal = ({ isOpen, booking, sessionNumber, onClose, therapistProfile = {}, userType = "therapist", user = {}, initialActivities = [] }) => {
    const [selectedSession, setSelectedSession] = useState(sessionNumber);
    const [activities, setActivities] = useState(initialActivities);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newActivity, setNewActivity] = useState({ title: "", category: "Mindfulness", description: "" });

    useEffect(() => {
        if (sessionNumber) {
            setSelectedSession(sessionNumber);
        }
    }, [sessionNumber]);

    useEffect(() => {
        if (isOpen && booking?.id) {
            // ALWAYS sync with latest data from parent dashboard if it exists
            const currentActivities = initialActivities.length > 0 ? initialActivities : [];
            if (currentActivities.length > 0 || !loading) {
                setActivities(currentActivities);
            }

            // Only fetch if we don't have data in the map yet
            if (currentActivities.length === 0) {
                fetchActivities();
            }
        }
    }, [isOpen, booking?.id, selectedSession, initialActivities]);

    const fetchActivities = async () => {
        setLoading(true);
        try {
            const activityDocId = `${booking.id}_session_${selectedSession}`;
            const activityDoc = await getDoc(doc(db, "session_activities", activityDocId));

            if (activityDoc.exists()) {
                setActivities(activityDoc.data().activities || []);
            } else {
                // Compatibility fallback
                const bookingDoc = await getDoc(doc(db, "bookings", booking.id));
                if (bookingDoc.exists()) {
                    const data = bookingDoc.data();
                    setActivities(data.activities?.[`session_${selectedSession}`] || []);
                } else {
                    setActivities([]);
                }
            }
        } catch (error) {
            console.error("Error fetching activities:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddActivity = async () => {
        if (userType === "patient") return;
        if (!newActivity.title.trim()) return;

        const activityToAdd = {
            id: Date.now().toString(),
            title: newActivity.title,
            category: newActivity.category || "General",
            description: newActivity.description || "",
            completed: false,
            createdAt: new Date().toISOString(),
            suggestedBy: "therapist",
            icon: newActivity.category?.includes("Mind") ? "🧘" :
                newActivity.category?.includes("Journal") ? "✍️" :
                    newActivity.category?.includes("Phys") ? "🚶" : "📞"
        };

        try {
            const activityDocId = `${booking.id}_session_${selectedSession}`;
            const activityRef = doc(db, "session_activities", activityDocId);
            const updatedActivities = [...activities, activityToAdd];

            await setDoc(activityRef, {
                activities: updatedActivities,
                bookingId: booking.id,
                sessionNumber: selectedSession,
                therapistId: booking.therapistId || user?.uid || "",
                therapistEmail: (user?.email || therapistProfile?.email || booking.therapistEmail || "").toLowerCase().trim(),
                therapistName: booking.therapistName || therapistProfile.displayName || "",
                patientId: booking.userId || "",
                patientName: booking.fullName || "",
                updatedAt: serverTimestamp()
            }, { merge: true });

            setActivities(updatedActivities);
            setNewActivity({ title: "", category: "Mindfulness", description: "" });
            setShowAddForm(false);
        } catch (error) {
            console.error("Error adding activity:", error);
        }
    };

    const handleDeleteActivity = async (activityId) => {
        if (userType === "patient") return;
        try {
            const activityDocId = `${booking.id}_session_${selectedSession}`;
            const activityRef = doc(db, "session_activities", activityDocId);
            const updatedActivities = activities.filter(a => a.id !== activityId);

            await updateDoc(activityRef, {
                activities: updatedActivities,
                updatedAt: serverTimestamp()
            });

            setActivities(updatedActivities);
        } catch (error) {
            console.error("Error deleting activity:", error);
        }
    };

    const handleToggleActivity = async (activityId) => {
        try {
            const activityDocId = `${booking.id}_session_${selectedSession}`;
            const activityRef = doc(db, "session_activities", activityDocId);
            const updatedActivities = activities.map(a =>
                a.id === activityId ? { ...a, completed: !a.completed, completedAt: !a.completed ? new Date().toISOString() : null } : a
            );

            await setDoc(activityRef, {
                activities: updatedActivities,
                bookingId: booking.id,
                sessionNumber: selectedSession,
                therapistId: booking.therapistId || user?.uid || "",
                therapistEmail: (user?.email || therapistProfile?.email || booking.therapistEmail || "").toLowerCase().trim(),
                therapistName: booking.therapistName || therapistProfile.displayName || "",
                patientId: booking.userId || "",
                patientName: booking.fullName || "",
                updatedAt: serverTimestamp()
            }, { merge: true });

            setActivities(updatedActivities);
        } catch (error) {
            console.error("Error toggling activity:", error);
        }
    };

    if (!isOpen || !booking) return null;

    const stats = {
        completed: activities.filter(a => a.completed).length,
        total: activities.length,
        rate: activities.length > 0 ? Math.round((activities.filter(a => a.completed).length / activities.length) * 100) : 0
    };

    return (
        <AnimatePresence>
            {isOpen && booking && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={styles.backdrop}
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="activity-modal"
                    >
                        <div className="activity-modal-header">
                            <div style={{ ...styles.modalHeaderBasic, marginBottom: '10px' }}>
                                <button onClick={onClose} className="back-btn-simple">
                                    <ArrowLeftIcon />
                                </button>
                                <h2 style={{ ...styles.notesMainTitle, margin: 0 }}>Client Activities</h2>
                                <div style={{ width: '28px' }}></div>
                            </div>
                        </div>

                        <div className="activity-modal-body">

                            {/* Session Tabs */}
                            <div className="modal-session-tabs">
                                {[...Array(parseInt(booking.sessionCount) || 1)].map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setSelectedSession(i + 1)}
                                        className={`modal-tab-btn ${selectedSession === i + 1 ? 'active' : ''}`}
                                    >
                                        Session {i + 1}
                                    </button>
                                ))}
                            </div>

                            <div className="modal-progress-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Activity Progress</h3>
                                    <span style={{ fontSize: '14px', color: '#C57B57', fontWeight: 'bold' }}>Session {selectedSession}</span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#003049' }}>{stats.completed}/{stats.total}</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Activities completed</div>
                                    </div>
                                    <div style={{ flex: 2 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{stats.rate}%</span>
                                            <span style={{ fontSize: '12px', color: '#666' }}>Completion rate</span>
                                        </div>
                                        <div style={{ width: '100%', height: '8px', backgroundColor: '#E0E0E0', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{
                                                width: `${stats.rate}%`,
                                                height: '100%',
                                                backgroundColor: '#2A9D8F',
                                                transition: 'width 0.5s ease-out'
                                            }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Activities</h3>
                                {userType === "therapist" && !showAddForm && (
                                    <button
                                        onClick={() => setShowAddForm(true)}
                                        style={{
                                            backgroundColor: '#F07167',
                                            color: 'white',
                                            border: 'none',
                                            padding: '8px 16px',
                                            borderRadius: '10px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <span>+ Add Activity</span>
                                    </button>
                                )}
                            </div>

                            {showAddForm && (
                                <motion.div
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{
                                        backgroundColor: '#FFF7F1',
                                        padding: '15px',
                                        borderRadius: '15px',
                                        marginBottom: '20px',
                                        border: '1px solid #FFDCC0',
                                        position: 'relative'
                                    }}
                                >
                                    <button
                                        onClick={() => setShowAddForm(false)}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            background: 'none',
                                            border: 'none',
                                            color: '#C57B57',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            fontWeight: 'bold'
                                        }}
                                    >✕</button>
                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Activity Title</label>
                                        <input
                                            type="text"
                                            value={newActivity.title}
                                            onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                                            placeholder="e.g. Morning Meditation"
                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #DDD' }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Category</label>
                                        <input
                                            type="text"
                                            value={newActivity.category}
                                            onChange={(e) => setNewActivity({ ...newActivity, category: e.target.value })}
                                            placeholder="e.g. Mindfulness"
                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #DDD' }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Short Description</label>
                                        <input
                                            type="text"
                                            value={newActivity.description}
                                            onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                                            placeholder="e.g. Focus on breath for 10 mins"
                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #DDD' }}
                                        />
                                    </div>
                                    <button
                                        onClick={handleAddActivity}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            backgroundColor: '#2A9D8F',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Confirm Add Activity
                                    </button>
                                </motion.div>
                            )}

                            <div className="activity-list">
                                {loading ? (
                                    <p style={{ textAlign: 'center', color: '#999' }}>Loading activities...</p>
                                ) : activities.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '30px', backgroundColor: '#F9F9F9', borderRadius: '15px', color: '#999' }}>
                                        <p>No activities added for this session yet.</p>
                                        <p style={{ fontSize: '12px' }}>Click "+ Add Activity" to get started.</p>
                                    </div>
                                ) : (
                                    activities.map(activity => {
                                        const getTagColor = (cat) => {
                                            switch (cat) {
                                                case 'Mindfulness': return { bg: '#F3E5F5', text: '#9C27B0' };
                                                case 'Physical': return { bg: '#E8F5E9', text: '#4CAF50' };
                                                case 'Journaling': return { bg: '#E3F2FD', text: '#2196F3' };
                                                case 'Social': return { bg: '#FCE4EC', text: '#E91E63' };
                                                default: return { bg: '#F5F5F5', text: '#757575' };
                                            }
                                        };
                                        const colors = getTagColor(activity.category);

                                        return (
                                            <div key={activity.id} className="activity-item">
                                                <div style={{ paddingTop: '2px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={activity.completed}
                                                        onChange={() => handleToggleActivity(activity.id)}
                                                        style={{
                                                            width: '18px',
                                                            height: '18px',
                                                            cursor: 'pointer',
                                                            accentColor: '#2A9D8F',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <h4 style={{
                                                            margin: '0 0 8px 0',
                                                            fontSize: '15px',
                                                            fontWeight: '600',
                                                            color: '#333',
                                                            textDecoration: activity.completed ? 'line-through' : 'none'
                                                        }}>{activity.title}</h4>
                                                        {userType === "therapist" && (
                                                            <button
                                                                onClick={() => handleDeleteActivity(activity.id)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    color: '#BBB',
                                                                    fontSize: '16px',
                                                                    padding: '0 4px'
                                                                }}
                                                                title="Delete activity"
                                                            >
                                                                🗑️
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                                        <span style={{
                                                            fontSize: '10px',
                                                            backgroundColor: colors.bg,
                                                            color: colors.text,
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            fontWeight: '600'
                                                        }}>{activity.category}</span>
                                                        <span style={{ fontSize: '10px', color: '#888' }}>Daily</span>
                                                        <span style={{ fontSize: '10px', color: '#888' }}>Suggested by {therapistProfile.displayName || "Therapist"}</span>
                                                    </div>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        lineHeight: '1.4'
                                                    }}>{activity.description}</p>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};
